"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

/**
 * Scroll-reveal driver for the marketing surface ("Site Spec" redesign).
 *
 * Server components mark elements with `data-reveal` (and optionally an
 * inline `--reveal-delay` for stagger); this component observes them and adds
 * `.is-in` when they enter the viewport. The hidden initial state lives in
 * globals.css behind `@media (scripting: enabled) and
 * (prefers-reduced-motion: no-preference)`, so crawlers, no-JS visitors and
 * reduced-motion users always see fully visible content — this component is
 * pure progressive enhancement.
 *
 * Mounted once in app/(marketing)/layout.tsx; re-runs on pathname change so
 * client-side navigations between marketing pages pick up the new page's
 * reveal targets (the layout itself persists across navigations).
 */
export function RevealInit() {
  const pathname = usePathname();

  useEffect(() => {
    const els = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]:not(.is-in)"),
    );
    if (els.length === 0) return;

    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      for (const el of els) el.classList.add("is-in");
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );

    for (const el of els) io.observe(el);
    return () => io.disconnect();
  }, [pathname]);

  return null;
}
